import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import { applySceneCropFraming } from '@/lib/crop/sceneCropFraming';
import { KEYFRAME_TRANSITION_EPS, round3 } from '@/lib/transitions/keyframeCuts';
import { MAX_TRANSITION_DURATION_SEC, type KeyframeTransition } from '@/lib/transitions/types';
import type { SceneLike } from '@/lib/transitions/buildSceneLayout';

// A camera move inside one scene (push-in, pull-out, pan) built from what the
// preview and the export already render: a chain of crop keyframes stepping
// from `from` to `to`, joined by camera-move keyframe transitions whose
// windows (centered on each boundary, at most 1.5 s) tile the scene, so the
// crop glides the whole way. No new render path, no backend change.

export interface CameraFraming { x: number; y: number; zoom: number }

/** The glides stop this far from the scene edges: a window reaching the cut
 *  would tween toward the next scene's crop. */
export const CAMERA_MOVE_EDGE_SECONDS = 2 / 30;
export const MIN_CAMERA_MOVE_SCENE_SECONDS = 0.5;

const lerp = (from: CameraFraming, to: CameraFraming, t: number): CameraFraming => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  zoom: from.zoom + (to.zoom - from.zoom) * t,
});

/** Keyframe transitions strictly inside `range` (not on its edges). */
export const isInsideRange = (transition: KeyframeTransition, range: { start: number; end: number }) => (
  transition.atTime > range.start + KEYFRAME_TRANSITION_EPS && transition.atTime < range.end - KEYFRAME_TRANSITION_EPS
);

/** Null when the scene is too short to move in. */
export function planCameraMove({
  keyframes, transitions, scenes, range, from, to,
}: {
  keyframes: CropCenterKeyframe[];
  transitions: readonly KeyframeTransition[];
  scenes: readonly SceneLike[];
  range: { start: number; end: number };
  from: CameraFraming;
  to: CameraFraming;
}): { keyframes: CropCenterKeyframe[]; transitions: KeyframeTransition[] } | null {
  const span = range.end - range.start - 2 * CAMERA_MOVE_EDGE_SECONDS;
  if (range.end - range.start < MIN_CAMERA_MOVE_SCENE_SECONDS || span <= 0) return null;
  const steps = Math.max(1, Math.ceil(span / MAX_TRANSITION_DURATION_SEC - 1e-9));
  const width = span / steps;
  const boundaries = Array.from({ length: steps }, (_, i) => range.start + CAMERA_MOVE_EDGE_SECONDS + width / 2 + i * width);
  const edges = [range.start, ...boundaries, range.end];
  const chain: CropCenterKeyframe[] = edges.slice(0, -1).map((time, i) => ({
    time,
    endTime: edges[i + 1],
    ...lerp(from, to, i / steps),
  }));
  // Split at the scene edges first, so nothing outside the scene changes.
  const outside = applySceneCropFraming({ keyframes, scenes, range, framing: from })
    .filter((keyframe) => keyframe.time < range.start || keyframe.endTime > range.end);
  return {
    keyframes: [...outside, ...chain].sort((a, b) => a.time - b.time),
    transitions: [
      ...transitions.filter((transition) => !isInsideRange(transition, range)),
      ...boundaries.map((at) => ({ atTime: round3(at), kind: 'camera-move' as const, durationSec: width })),
    ].sort((a, b) => a.atTime - b.atTime),
  };
}
